<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import CodexHeading from "@/components/CodexRail/CodexHeading.vue"
import RPGStats from "@/components/RPGStats/RPGStats.vue"
import RpgEffectHolder from "@/components/PlayerRPGBars/RPGEffectHolder.vue"
import ModificatorTag from "@/components/Player/ModificatorTag.vue"
import { CharacterGameApi, ModificatorsGameApi } from "@/api/backendService.js"
import StatsGameService from "@/services/statService.js"
import { avatarPlaceholderUrl } from "@/utils/avatarPlaceholder"
import { fallbackOnError } from "@/utils/imageFallback"
import type { ActiveEffect, CharacterModificator } from "@/api/dx-backend"

interface Player {
  id: string
  name: string
  biography: {
    avatar?: string
    gender?: string
  }
  path: {
    id: string
    name: string
    description: string
    icon: string
  }
  rank: {
    name: string
    level: number
  }
  experience: number
  tags: string[]
  resetting_base_stats?: boolean
}

interface Stat {
  id: string
  name: string
  dice_rolls: any[]
  additional_value: number
  base_value?: number
}

interface Props {
  isOpen?: boolean
  /*
   * The dossier does not fetch its own effects: the location view already
   * holds the same `/api/effects/active/` list for the HUD bars, and a second
   * request would let the two panels disagree mid-cycle.
   */
  effects?: ActiveEffect[]
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  effects: () => []
})

const emit = defineEmits<{
  close: []
  upgradeStat: [statId: string]
}>()

const { t } = useI18n()

const loading = ref(true)
const playerInfo = ref<Player | null>(null)
const playerStats = ref<Stat[]>([])
const upgradePoints = ref(0)
const modificators = ref<CharacterModificator[]>([])

const isModalOpen = computed(() => props.isOpen)

const avatarPlaceholder = computed(() =>
  avatarPlaceholderUrl(playerInfo.value?.biography?.gender)
)

const avatarSrc = computed(() =>
  playerInfo.value?.biography?.avatar || avatarPlaceholder.value
)

const onAvatarError = (event: Event) => fallbackOnError(event, avatarPlaceholder.value)

const activeEffects = computed(() => props.effects ?? [])

const rankLine = computed(() => {
  const rank = playerInfo.value?.rank
  if (!rank) return ''
  return rank.level === undefined || rank.level === null
    ? rank.name
    : `${rank.name} · ${t('player.profile.levelShort')} ${rank.level}`
})

const handleClose = () => {
  emit('close')
}

const handleUpgradeStat = (statId: string) => {
  const stat = playerStats.value.find(s => s.id === statId)
  if (stat && upgradePoints.value > 0) {
    stat.additional_value++
    upgradePoints.value--
    emit('upgradeStat', statId)
  }
}

const handleSwitchBaseStats = async ({ fromStatId, toStatId }: { fromStatId: string, toStatId: string }) => {
  try {
    const response = await CharacterGameApi.characterStatsSwipeBaseStatCreate({
      from_stat: fromStatId,
      to_stat: toStatId,
    })
    playerStats.value = response.data
  } catch (error) {
    console.error("Failed to switch base stats:", error)
  }
}

const syncPlayerData = async () => {
  try {
    loading.value = true

    // Initialize stats service mapping for images
    await StatsGameService.refreshStats()

    // Fetch player info
    const playerResponse = await CharacterGameApi.characterPlayerCharacterDetailsRetrieve()
    playerInfo.value = playerResponse.data

    // Fetch player stats
    const statsResponse = await CharacterGameApi.characterStatsList()
    playerStats.value = statsResponse.data

    try {
      modificators.value = (await ModificatorsGameApi.modificatorsCharacterList()).data
    } catch (error) {
      console.error("Failed to fetch character modificators:", error)
      modificators.value = []
    }

  } catch (error) {
    console.error("Failed to fetch player data:", error)
  } finally {
    loading.value = false
  }
}

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') handleClose()
}

// Watch for modal opening to fetch data
watch(() => props.isOpen, (newValue) => {
  if (newValue) {
    syncPlayerData()
    window.addEventListener('keydown', onKeydown)
  } else {
    window.removeEventListener('keydown', onKeydown)
  }
}, { immediate: true })

onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
</script>

<template>
  <Transition name="dossier-fade">
    <div v-if="isModalOpen" class="modal-overlay" @click.self="handleClose">
      <div
        class="dossier"
        role="dialog"
        aria-modal="true"
        :aria-label="t('player.profile.title')"
      >
        <span class="dossier-corner dossier-corner--tl" aria-hidden="true"></span>
        <span class="dossier-corner dossier-corner--tr" aria-hidden="true"></span>
        <span class="dossier-corner dossier-corner--bl" aria-hidden="true"></span>
        <span class="dossier-corner dossier-corner--br" aria-hidden="true"></span>

        <header class="dossier-head">
          <CodexHeading :label="t('player.profile.title')" tone="gold" spine="head"/>
          <button
            class="close-btn"
            type="button"
            :title="t('player.common.close')"
            :aria-label="t('player.common.close')"
            @click="handleClose"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </header>

        <div class="dossier-body" v-if="!loading && playerInfo">
          <aside class="identity">
            <div class="plate">
              <img :src="avatarSrc" :alt="playerInfo.name" class="plate-art" @error="onAvatarError"/>
              <span class="plate-scan" aria-hidden="true"></span>
              <span class="plate-fade" aria-hidden="true"></span>

              <div
                class="sigil"
                :title="`${playerInfo.path.name}: ${playerInfo.path.description}`"
              >
                <img :src="playerInfo.path.icon" :alt="playerInfo.path.name" class="sigil-icon"/>
              </div>
            </div>

            <h2 class="identity-name">{{ playerInfo.name }}</h2>
            <p class="identity-path">{{ playerInfo.path.name }}</p>

            <dl class="readout">
              <div class="readout-row">
                <dt class="readout-label">{{ t('player.profile.rank') }}</dt>
                <dd class="readout-value">{{ rankLine }}</dd>
              </div>
              <div class="readout-row">
                <dt class="readout-label">{{ t('player.profile.experience') }}</dt>
                <dd class="readout-value readout-value--mono">
                  {{ playerInfo.experience }}<span class="readout-unit">{{ t('player.profile.xp') }}</span>
                </dd>
              </div>
            </dl>

            <div class="tags" v-if="playerInfo.tags && playerInfo.tags.length > 0">
              <span class="tag" v-for="tag in playerInfo.tags" :key="tag">{{ tag }}</span>
            </div>

            <section class="dossier-block" v-if="activeEffects.length > 0">
              <h3 class="dossier-block-title">{{ t('player.profile.effects') }}</h3>
              <div class="effects-row">
                <RpgEffectHolder
                    v-for="effect in activeEffects"
                    :key="effect.id"
                    :effect="effect"
                />
              </div>
            </section>

            <section class="dossier-block" v-if="modificators.length > 0">
              <h3 class="dossier-block-title">{{ t('player.profile.modificators') }}</h3>
              <ul class="modificator-tags">
                <ModificatorTag
                    v-for="entry in modificators"
                    :key="entry.id"
                    :modificator="entry.modificator"
                />
              </ul>
            </section>
          </aside>

          <section class="attributes">
            <RPGStats
              :stats="playerStats"
              :title="t('player.profile.attributes')"
              :show-upgrades="true"
              :available-upgrade-points="upgradePoints"
              :allowResetBaseStats="playerInfo?.resetting_base_stats"
              @upgrade-stat="handleUpgradeStat"
              @switchBaseStats="handleSwitchBaseStats"
            />
          </section>
        </div>

        <div class="dossier-loading" v-else-if="loading">
          <span class="dx-loader" aria-hidden="true"></span>
          <p class="loading-text">{{ t('player.profile.loading') }}</p>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  background:
      radial-gradient(ellipse at center, rgba(3, 11, 16, 0.72) 0%, rgba(2, 6, 9, 0.92) 100%);
  backdrop-filter: blur(6px) saturate(0.85);
  -webkit-backdrop-filter: blur(6px) saturate(0.85);
  /* Above the action bar (2000), below the dice roller (10000). */
  z-index: 9000;
}

.dossier {
  --hud-cyan: #3ee9f0;
  --hud-cyan-dim: rgba(62, 233, 240, 0.45);
  --hud-gold: #f2c489;
  --hud-line: rgba(0, 255, 255, 0.18);
  --hud-slab: rgba(11, 18, 24, 0.72);
  --hud-slab-hi: rgba(16, 26, 34, 0.9);
  --hud-text: #d3e0e6;
  --hud-text-dim: #7f929c;
  --hud-halo: 0 1px 2px rgba(0, 10, 14, 0.95), 0 0 8px rgba(0, 10, 14, 0.85);
  --codex-spine-w: 14px;
  --codex-spine-gap: 0.9rem;
  --codex-spine: rgba(96, 166, 184, 0.85);

  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 60rem;
  max-height: 92vh;
  padding: 1.1rem 1.35rem 1.35rem;
  color: var(--hud-text);
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  background:
      linear-gradient(160deg, rgba(12, 19, 25, 0.96) 0%, rgba(6, 10, 14, 0.985) 100%);
  border: 1px solid var(--hud-line);
  border-radius: 0.375rem;
  box-shadow:
      0 24px 64px rgba(0, 0, 0, 0.72),
      inset 0 0 0 1px rgba(255, 255, 255, 0.02),
      inset 0 1px 0 rgba(62, 233, 240, 0.06);
}

/* The grid wash is what ties the panel to the tactical map behind it. */
.dossier::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background-image:
      linear-gradient(rgba(62, 233, 240, 0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(62, 233, 240, 0.035) 1px, transparent 1px);
  background-size: 2.25rem 2.25rem;
  mask-image: radial-gradient(ellipse at 50% 0%, #000 0%, transparent 78%);
  pointer-events: none;
}

.dossier-corner {
  position: absolute;
  width: 0.85rem;
  height: 0.85rem;
  border: 1px solid var(--hud-cyan-dim);
  pointer-events: none;
}

.dossier-corner--tl { top: -1px; left: -1px; border-width: 1px 0 0 1px; }
.dossier-corner--tr { top: -1px; right: -1px; border-width: 1px 1px 0 0; }
.dossier-corner--bl { bottom: -1px; left: -1px; border-width: 0 0 1px 1px; }
.dossier-corner--br { bottom: -1px; right: -1px; border-width: 0 1px 1px 0; }

.dossier-head {
  position: relative;
  z-index: 1;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 0.9rem;
}

.dossier-head > :first-child {
  flex: 1 1 auto;
  min-width: 0;
}

.close-btn {
  flex: 0 0 auto;
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--hud-text-dim);
  font-size: 0.7rem;
  line-height: 1;
  background: rgba(13, 13, 13, 0.82);
  border: 1px solid var(--hud-line);
  border-radius: 0.25rem;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
}

.close-btn:hover {
  color: var(--hud-cyan);
  border-color: var(--hud-cyan-dim);
  background: rgba(62, 233, 240, 0.08);
}

.close-btn:focus-visible {
  outline: 1px solid var(--hud-cyan);
  outline-offset: 2px;
}

.dossier-body {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 15.5rem minmax(0, 1fr);
  gap: 1.25rem;
  min-height: 0;
}

/* ---- identity column ---- */

.identity {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  min-width: 0;
}

.plate {
  position: relative;
  /* Grows to whatever height the attribute column sets, so the two columns end together. */
  flex: 1 1 auto;
  min-height: 14rem;
  overflow: hidden;
  background: #070b0e;
  border: 1px solid var(--hud-line);
  border-radius: 0.25rem;
  /* Clipped top-right corner - the same angular cut the tactical HUD uses. */
  clip-path: polygon(0 0, calc(100% - 1.1rem) 0, 100% 1.1rem, 100% 100%, 0 100%);
}

.plate-art {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(0.9) contrast(1.05);
}

.plate-scan {
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0) 0 2px,
      rgba(0, 12, 16, 0.22) 2px 3px
  );
  pointer-events: none;
}

.plate-fade {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 45%;
  background: linear-gradient(to top, rgba(5, 9, 12, 0.92), transparent);
  pointer-events: none;
}

.sigil {
  position: absolute;
  right: 0.5rem;
  bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  background: rgba(6, 10, 14, 0.88);
  border: 1px solid var(--hud-cyan-dim);
  border-radius: 50%;
  cursor: help;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.sigil:hover {
  border-color: var(--hud-cyan);
  box-shadow: 0 0 0.75rem rgba(62, 233, 240, 0.28);
}

.sigil-icon {
  width: 1.5rem;
  height: 1.5rem;
  object-fit: cover;
  border-radius: 50%;
}

.identity-name {
  margin: 0.15rem 0 0;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--hud-gold);
  text-shadow: var(--hud-halo);
  overflow-wrap: anywhere;
}

.identity-path {
  margin: 0;
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--hud-text-dim);
}

.readout {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin: 0.25rem 0 0;
  background: var(--hud-line);
  border: 1px solid var(--hud-line);
  border-radius: 0.25rem;
  overflow: hidden;
}

.readout-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.4rem 0.55rem;
  background: var(--hud-slab);
}

.readout-label {
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.56rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--hud-text-dim);
}

.readout-value {
  margin: 0;
  font-size: 0.76rem;
  font-weight: 600;
  text-align: right;
  color: var(--hud-text);
}

.readout-value--mono {
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  color: var(--hud-cyan);
}

.readout-unit {
  margin-left: 0.2rem;
  font-size: 0.56rem;
  letter-spacing: 0.12em;
  color: var(--hud-text-dim);
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.tag {
  padding: 0.15rem 0.4rem;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.55rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--hud-cyan);
  background: rgba(62, 233, 240, 0.07);
  border: 1px solid var(--hud-line);
  border-radius: 0.15rem;
}

.dossier-block {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.dossier-block-title {
  margin: 0;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.56rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--hud-text-dim);
}

.effects-row {
  display: flex;
  flex-flow: row wrap;
  gap: 0.4rem;
}

.modificator-tags {
  display: flex;
  flex-flow: row wrap;
  gap: 0.25rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* ---- attributes column ---- */

.attributes {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.attributes > :deep(.rpg-stats-container) {
  flex: 1 1 auto;
  min-height: 0;
}

/* ---- loading ---- */

.dossier-loading {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 16rem;
}

.loading-text {
  margin: 0;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--hud-text-dim);
}

.dossier-fade-enter-active,
.dossier-fade-leave-active {
  transition: opacity 0.18s ease;
}

.dossier-fade-enter-active .dossier,
.dossier-fade-leave-active .dossier {
  transition: transform 0.18s cubic-bezier(0.2, 0, 0.2, 1);
}

.dossier-fade-enter-from,
.dossier-fade-leave-to {
  opacity: 0;
}

.dossier-fade-enter-from .dossier,
.dossier-fade-leave-to .dossier {
  transform: translateY(0.75rem);
}

@media (prefers-reduced-motion: reduce) {
  .dossier-fade-enter-active,
  .dossier-fade-leave-active,
  .dossier-fade-enter-active .dossier,
  .dossier-fade-leave-active .dossier {
    transition: none;
  }
}

@media (max-width: 820px) {
  .dossier {
    padding: 0.9rem 1rem 1.1rem;
    overflow-y: auto;
  }

  .dossier-body {
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
  }

  .identity {
    display: grid;
    grid-template-columns: 8rem minmax(0, 1fr);
    grid-template-rows: auto auto auto auto;
    align-content: start;
    column-gap: 0.85rem;
  }

  .plate {
    grid-row: 1 / span 4;
    aspect-ratio: 3 / 4;
    min-height: 0;
  }

  .identity-name {
    align-self: end;
  }
}
</style>
